import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import TestBox from "../../common/testBox/testBox";
import CustomButton from "../../common/customButton/customButton";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import { useSharedState } from "../../context/formsContext";
import testOne from "../../assets/Maths-Paper-1-Cover_1.png";
import testTwo from "../../assets/Maths-Paper-2-Cover_1.png";
import testThree from "../../assets/Comprehension-Paper-Cover_1.png";
import testFour from "../../assets/NVR-Paper-Cover-1_1.png";
import testFive from "../../assets/NVR-Paper-2-Cover_1.png";
import testSix from "../../assets/Verbal-GL-Paper-Cover-New.png";
import testSeven from "../../assets/NVRSpatial-GL-Paper-Cover_1.png";
import testEight from "../../assets/TechnicalEnglishp-paper-cover-GL.png";
import genericTestImage from "../../assets/Personalised_tests-01.png";
import rightArrow from "../../assets/rightArrow.png";
import "./freeDownloadsOnlyPage.css";

const FreeDownloadsOnlyPage = () => {
  const [isFreeDownloads, setIsFreeDownloads] = useState(true);
  const navigate = useNavigate();
  const { value, setValue } = useSharedState();

  // Ensure email is stored in localStorage when component mounts
  useEffect(() => {
    // Check multiple sources for email
    const localEmail = localStorage.getItem('email');
    const mentorEmail = localStorage.getItem('mentorEmail');
    const valueEmail = value.email;
    
    // Priority: value.email > mentorEmail > localStorage email
    let emailToStore = null;
    if (valueEmail && valueEmail.trim()) {
      emailToStore = valueEmail.trim();
    } else if (mentorEmail && mentorEmail.trim()) {
      emailToStore = mentorEmail.trim();
    } else if (localEmail && localEmail.trim()) {
      // Try to parse if it's JSON stringified
      try {
        const parsed = JSON.parse(localEmail);
        emailToStore = typeof parsed === 'string' ? parsed.trim() : localEmail.trim();
      } catch (e) {
        emailToStore = localEmail.trim();
      }
    }
    
    // Store email in localStorage if we found one
    if (emailToStore) {
      localStorage.setItem('email', emailToStore);
      // Also set in context if not already set
      if (!value.email || value.email !== emailToStore) {
        setValue((prevValue) => ({
          ...prevValue,
          email: emailToStore,
        }));
      }
    }
  }, [value.email, setValue]);

  const freePapers = [
    {
      type: "maths",
      id: 1,
      name: "KSOL 11+ Practice Papers | Maths Paper 1 - Suitable for the 11+ GL Assessment",
      img: testOne,
    },
    {
      type: "maths",
      id: 2,
      name: "KSOL 11+ Practice Papers | Maths Paper 2 - Suitable for the 11+ GL Assessment",
      img: testTwo,
    },
    {
      type: "english",
      id: 3,
      name: "KSOL 11+ Practice Papers | Comprehension - Suitable for the 11+ GL Assessment",
      img: testThree,
    },
    {
      type: "nonVerbal",
      id: 4,
      name: "KSOL 11+ Practice Papers | Non-Verbal Reasoning Paper 1 - Suitable for the 11+ GL Assessment",
      img: testFour,
    },
    {
      type: "nonVerbal",
      id: 5,
      name: "KSOL 11+ Practice Papers | Non-Verbal Reasoning Paper 2 - Suitable for the 11+ GL Assessment",
      img: testFive,
    },
    {
      type: "verbal",
      id: 6,
      name: "KSOL 11+ Practice Papers | Verbal Reasoning - Suitable for the 11+ GL Assessment ",
      img: testSix,
    },
    {
      type: "nonVerbal",
      id: 7,
      name: "KSOL 11+ Practice Papers | NVR Spatial Reasoning - Suitable for the 11+ GL Assessment ",
      img: testSeven,
    },
    {
      type: "english",
      id: 8,
      name: "KSOL 11+ Practice Paper | Technical English Paper - Suitable for the 11+ GL Assessment ",
      img: testEight,
    },
  ];

  const setFreeDownloadsBackground = () => {
    if (isFreeDownloads) {
      return "green-bg-selected text-white h-12";
    } else {
      return "green-bg h-10";
    }
  };

  const setFiveMinuteTestBackground = () => {
    if (!isFreeDownloads) {
      return "orange-bg-selected text-white h-12";
    } else {
      return "orange-bg h-10";
    }
  };

  const handleAccessFreeTests = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    // Get email from multiple sources and ensure it's stored in localStorage
    const localEmail = localStorage.getItem('email');
    const mentorEmail = localStorage.getItem('mentorEmail');
    const valueEmail = value.email;
    
    // Priority: value.email > mentorEmail > localStorage email
    let emailToStore = null;
    if (valueEmail && valueEmail.trim()) {
      emailToStore = valueEmail.trim();
    } else if (mentorEmail && mentorEmail.trim()) {
      emailToStore = mentorEmail.trim();
    } else if (localEmail && localEmail.trim()) {
      // Try to parse if it's JSON stringified
      try {
        const parsed = JSON.parse(localEmail);
        emailToStore = typeof parsed === 'string' ? parsed.trim() : localEmail.trim();
      } catch (e) {
        emailToStore = localEmail.trim();
      }
    }
    
    // Store email in localStorage before navigating
    if (emailToStore) {
      localStorage.setItem('email', emailToStore);
    }
    
    // For iframe embedding, open in parent window or new tab
    if (window.parent !== window.self) {
      // We're in an iframe, try to communicate with parent or open in new tab
      try {
        window.parent.postMessage({ type: 'navigate', path: '/FreeDownloads/parentRegistration' }, '*');
        // Also navigate within the iframe
        navigate("/parentRegistration");
      } catch (e) {
        // Fallback: navigate within iframe
        navigate("/parentRegistration");
      }
    } else {
      // Not in iframe, use normal navigation
      navigate("/parentRegistration");
    }
  };

  return (
    <>
      <Helmet>
        <title>Free Downloads | KSOL</title>
        <meta name="description" content="Access your free 11+ practice papers and benchmark tests. Download papers and take timed tests to assess your child's 11+ readiness." />
        <meta name="keywords" content="free 11+ papers, 11+ downloads, 11+ practice tests, GL exam papers, KSOL free resources" />
        <meta name="robots" content="noindex, nofollow" />
        <meta property="og:title" content="Free 11+ Downloads | KSOL" />
        <meta property="og:description" content="Access your free 11+ practice papers and benchmark tests." />
        <meta property="og:type" content="website" />
      </Helmet>
      <div 
        className="p-10 flex flex-col items-center justify-center bg-image"
        style={{ pointerEvents: 'auto' }}
      >
      <div 
        className="flex flex-col justify-between items-center w-full"
        style={{ pointerEvents: 'auto' }}
      >
        <div className="flex w-full justify-center">
          <div className="hidden flex xl:flex xl:flex justify-center items-end xl:w-3/4">
            <div
              onClick={() => setIsFreeDownloads(false)}
              className={`xl:w-2/4 flex justify-center items-center ${setFiveMinuteTestBackground()} px-4 left-tab cursor-pointer`}
            >
              <h3 className="text-white">5 Minute Tests</h3>
            </div>
            <div
              onClick={() => setIsFreeDownloads(true)}
              className={`xl:w-2/4 flex justify-center items-center ${setFreeDownloadsBackground()} px-4 right-tab cursor-pointer`}
            >
              <h3 className="text-white">Free Downloads</h3>
            </div>
          </div>
          <div className="flex-col xl:hidden w-full lg:w-auto">
            <div
              onClick={() => setIsFreeDownloads(false)}
              className={`flex justify-between items-center ${setFiveMinuteTestBackground()} px-4 left-tab-mobile cursor-pointer`}
            >
              <h3 style={{ fontSize: "28px" }} className="text-white">
                5 Minute Tests
              </h3>
              <img
                style={{ height: "30px" }}
                src={rightArrow}
                alt="right arrow"
              />
            </div>
            <div
              onClick={() => setIsFreeDownloads(true)}
              className={`flex justify-between items-center ${setFreeDownloadsBackground()} px-4 right-tab-mobile cursor-pointer`}
            >
              <h3 style={{ fontSize: "28px" }} className="text-white">
                Free Downloads
              </h3>
              <img
                style={{ height: "30px" }}
                src={rightArrow}
                alt="right arrow"
              />
            </div>
          </div>
        </div>

        <div className="w-full bg-white drop-shadow-lg border-radius-bottom ml-auto mr-auto xl:w-3/4">
          {!isFreeDownloads && (
            <p className="p-4">
              Welcome! Get access to our 5 minute tests across all GL Assessment subjects.
            </p>
          )}
          {isFreeDownloads && (
            <p className="p-4">
              Welcome! Click on one of the tests below to download a practice paper.
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col w-full items-start xl:w-3/4 mt-4">
        {!isFreeDownloads && (
          <div className="text-left flex justify-start items-center w-full mb-4">
            <h2 className="text-left">Select Test</h2>
          </div>
        )}
        {isFreeDownloads && (
          <div className="text-left flex justify-start items-center w-full mb-4">
            <h2 className="text-left">Download A Paper</h2>
          </div>
        )}

        {!isFreeDownloads && (
          <div 
            className="w-full mb-4"
            style={{ 
              pointerEvents: 'auto',
              position: 'relative',
              zIndex: 1
            }}
          >
            <div 
              className="w-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 xl:p-12 border-2 border-dashed border-gray-300"
              style={{ pointerEvents: 'auto' }}
            >
              <div 
                className="flex flex-col items-center justify-center text-center space-y-6"
                style={{ pointerEvents: 'auto' }}
              >
                <div className="relative" style={{ pointerEvents: 'none' }}>
                  <img
                    src={genericTestImage}
                    alt="test paper"
                    className="w-48 xl:w-64 h-auto rounded-3xl shadow-lg opacity-90"
                    style={{ pointerEvents: 'none' }}
                  />
                  <div 
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ pointerEvents: 'none' }}
                  >
                    <div className="bg-white bg-opacity-80 rounded-full p-3" style={{ pointerEvents: 'none' }}>
                      <svg
                        className="w-12 h-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        style={{ pointerEvents: 'none' }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="space-y-3 max-w-md">
                  <h3 className="text-2xl xl:text-3xl font-bold text-gray-800">
                    Unlock 5 Minute Tests
                  </h3>
                  <p className="text-gray-600 text-lg">
                    Complete your registration to access interactive 5-minute tests and track your child's progress.
                  </p>
                </div>
                <div 
                  className="pt-4"
                  style={{ 
                    pointerEvents: 'auto',
                    position: 'relative',
                    zIndex: 100
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <CustomButton
                    onClick={(e) => {
                      handleAccessFreeTests(e);
                    }}
                    text="Access Free Tests"
                    type={isMobile ? "logOut" : ""}
                  />
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500 pt-2">
                  <svg
                    className="w-5 h-5 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>Free registration • No card required</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {isFreeDownloads && (
          <div className="flex w-full flex-wrap xl:grid xl:grid-cols-3 xl:gap-4 mb-4">
            {freePapers.map((paper) => (
              <TestBox
                key={paper.id}
                title={paper.name}
                paperImg={paper.img}
                type={paper.type}
                freePapers={true}
                isMobile={isMobile}
              />
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default FreeDownloadsOnlyPage;
